import { useCallback } from 'react';
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import {
  selectUser,
  selectAccount,
  selectAccessToken,
  selectRefreshToken,
  selectJwtPayload,
  selectIsAuthenticated,
  selectIsLoading,
  selectIsInitialized,
  selectIsApiConnected,
  selectApiStatus,
  selectError,
  selectLastError,
  selectUserRole,
  selectUserAccountId,
  selectUserFeatures,
  selectUserPlan,
  selectPermissionValidator,
  selectTokens,
  selectIsSuperAdmin,
  selectIsAdmin,
  selectIsModerator,
  selectCanSeeAuthDebug,
  selectAuthStatus,
  selectApiConnectionStatus,
} from '../features/auth/authSelectors';
import {
  loginThunk,
  registerThunk,
  refreshTokenThunk,
  logoutThunk,
  logoutAllThunk,
  checkAuthThunk,
  createAccountThunk,
  updateProfileThunk,
  changePasswordThunk,
  checkApiStatusThunk,
} from '../features/auth/authThunks';
import {
  clearError,
  clearLastError,
  setLoading,
  setApiStatus,
  updateTokens,
  clearAuth,
  setInitialized,
} from '../features/auth/authSlice';
import {
  LoginCredentials,
  RegisterData,
  CreateAccountData,
  UpdateProfileData,
  ChangePasswordData,
} from '../features/auth/types';
import {
  checkRole,
  checkPermission,
  checkFeature,
  checkAccess,
  checkUserManagement,
  checkSameAccount,
  Role,
} from '../utils/permissions';

// ===== MAIN AUTH HOOK =====

export const useAuth = () => {
  const dispatch = useAppDispatch();
  
  // ===== SELECTORS =====
  
  // Basic data
  const user = useAppSelector(selectUser);
  const account = useAppSelector(selectAccount);
  const accessToken = useAppSelector(selectAccessToken);
  const refreshToken = useAppSelector(selectRefreshToken);
  const jwtPayload = useAppSelector(selectJwtPayload);
  const tokens = useAppSelector(selectTokens);
  
  // States
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  const isInitialized = useAppSelector(selectIsInitialized);
  
  // API status
  const isApiConnected = useAppSelector(selectIsApiConnected);
  const apiStatus = useAppSelector(selectApiStatus);
  
  // Errors
  const error = useAppSelector(selectError);
  const lastError = useAppSelector(selectLastError);
  
  // User properties
  const userRole = useAppSelector(selectUserRole);
  const userAccountId = useAppSelector(selectUserAccountId);
  const userFeatures = useAppSelector(selectUserFeatures);
  const userPlan = useAppSelector(selectUserPlan);
  
  // Permission validator
  const permissionValidator = useAppSelector(selectPermissionValidator);
  
  // Computed states
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const isAdmin = useAppSelector(selectIsAdmin);
  const isModerator = useAppSelector(selectIsModerator);
  const canSeeAuthDebug = useAppSelector(selectCanSeeAuthDebug);
  
  // Status objects
  const authStatus = useAppSelector(selectAuthStatus);
  const apiConnectionStatus = useAppSelector(selectApiConnectionStatus);
  
  // ===== ACTIONS =====
  
  // Authentication actions
  const login = useCallback(async (email: string, password: string) => {
    const credentials: LoginCredentials = { email, password };
    const result = await dispatch(loginThunk(credentials));
    return result;
  }, [dispatch]);
  
  const register = useCallback(async (userData: RegisterData) => {
    const result = await dispatch(registerThunk(userData));
    return result;
  }, [dispatch]);
  
  const logout = useCallback(async () => {
    const result = await dispatch(logoutThunk());
    return result;
  }, [dispatch]);
  
  const logoutAll = useCallback(async () => {
    const result = await dispatch(logoutAllThunk());
    return result;
  }, [dispatch]);
  
  const refreshTokenAction = useCallback(async () => {
    const result = await dispatch(refreshTokenThunk());
    return result;
  }, [dispatch]);
  
  const checkAuth = useCallback(async () => {
    const result = await dispatch(checkAuthThunk());
    return result;
  }, [dispatch]);
  
  // Account actions
  const createAccount = useCallback(async (accountData: CreateAccountData) => {
    const result = await dispatch(createAccountThunk(accountData));
    return result;
  }, [dispatch]);
  
  // Profile actions
  const updateProfile = useCallback(async (profileData: UpdateProfileData) => {
    const result = await dispatch(updateProfileThunk(profileData));
    return result;
  }, [dispatch]);
  
  const changePassword = useCallback(async (passwordData: ChangePasswordData) => {
    const result = await dispatch(changePasswordThunk(passwordData));
    return result;
  }, [dispatch]);
  
  // API actions
  const checkApiStatus = useCallback(async () => {
    const result = await dispatch(checkApiStatusThunk());
    return result;
  }, [dispatch]);
  
  // State management actions
  const clearErrorAction = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);
  
  const clearLastErrorAction = useCallback(() => {
    dispatch(clearLastError());
  }, [dispatch]);
  
  const setLoadingAction = useCallback((loading: boolean) => {
    dispatch(setLoading(loading));
  }, [dispatch]);
  
  const clearAuthAction = useCallback(() => {
    dispatch(clearAuth());
  }, [dispatch]);
  
  // ===== PERMISSION HELPERS =====
  
  const hasRole = useCallback((role: Role, exact: boolean = false) => {
    return checkRole(userRole, role, exact);
  }, [userRole]);
  
  const hasPermission = useCallback((permission: string) => {
    return checkPermission(userRole, permission);
  }, [userRole]);
  
  const hasFeature = useCallback((feature: string) => {
    return checkFeature(userFeatures, feature);
  }, [userFeatures]);
  
  const canAccess = useCallback((resource: string, action: string = 'read') => {
    return checkAccess(userRole, userFeatures, resource, action);
  }, [userRole, userFeatures]);
  
  const canManageUser = useCallback((targetRole: string, targetAccountId: string) => {
    return checkUserManagement(userRole, userAccountId, targetRole, targetAccountId);
  }, [userRole, userAccountId]);
  
  const isInSameAccount = useCallback((targetAccountId: string) => {
    return checkSameAccount(userAccountId, targetAccountId);
  }, [userAccountId]);
  
  // ===== DEBUG HELPER =====
  
  const debugAuth = useCallback(() => {
    console.group('🔍 Auth Debug Info (Redux)');
    console.log('User:', user);
    console.log('Account:', account);
    console.log('JWT Payload:', jwtPayload);
    console.log('Tokens:', tokens);
    console.log('Permission Validator:', permissionValidator);
    console.log('Auth Status:', authStatus);
    console.log('API Status:', apiConnectionStatus);
    console.groupEnd();
  }, [user, account, jwtPayload, tokens, permissionValidator, authStatus, apiConnectionStatus]);
  
  // ===== RETURN OBJECT =====
  
  return {
    // ===== DATA =====
    user,
    account,
    accessToken,
    refreshToken,
    jwtPayload,
    tokens,
    permissionValidator,
    
    // ===== STATES =====
    isAuthenticated,
    isLoading,
    isInitialized,
    isApiConnected,
    apiStatus,
    
    // ===== ERRORS =====
    error,
    lastError,
    
    // ===== USER PROPERTIES =====
    userRole,
    userAccountId,
    userFeatures,
    userPlan,
    
    // ===== COMPUTED STATES =====
    isSuperAdmin,
    isAdmin,
    isModerator,
    canSeeAuthDebug,
    
    // ===== STATUS OBJECTS =====
    authStatus,
    apiConnectionStatus,
    
    // ===== AUTHENTICATION ACTIONS =====
    login,
    register,
    logout,
    logoutAll,
    refreshTokenAction, // Renamed to avoid conflict
    checkAuth,
    
    // ===== ACCOUNT ACTIONS =====
    createAccount,
    
    // ===== PROFILE ACTIONS =====
    updateProfile,
    changePassword,
    
    // ===== API ACTIONS =====
    checkApiStatus,
    
    // ===== STATE MANAGEMENT ACTIONS =====
    clearError: clearErrorAction,
    clearLastError: clearLastErrorAction,
    setLoading: setLoadingAction,
    clearAuth: clearAuthAction,
    
    // ===== PERMISSION HELPERS =====
    hasRole,
    hasPermission,
    hasFeature,
    canAccess,
    canManageUser,
    isInSameAccount,
    
    // ===== DEBUG =====
    debugAuth,
  };
};

// ===== SPECIALIZED HOOKS =====

// Hook for permission checks only
export const usePermissions = () => {
  const userRole = useAppSelector(selectUserRole);
  const userFeatures = useAppSelector(selectUserFeatures);
  const userAccountId = useAppSelector(selectUserAccountId);
  
  return {
    hasRole: useCallback((role: Role, exact: boolean = false) => 
      checkRole(userRole, role, exact), [userRole]),
    hasPermission: useCallback((permission: string) => 
      checkPermission(userRole, permission), [userRole]),
    hasFeature: useCallback((feature: string) => 
      checkFeature(userFeatures, feature), [userFeatures]),
    canAccess: useCallback((resource: string, action: string = 'read') => 
      checkAccess(userRole, userFeatures, resource, action), [userRole, userFeatures]),
    canManageUser: useCallback((targetRole: string, targetAccountId: string) => 
      checkUserManagement(userRole, userAccountId, targetRole, targetAccountId), [userRole, userAccountId]),
    isInSameAccount: useCallback((targetAccountId: string) => 
      checkSameAccount(userAccountId, targetAccountId), [userAccountId]),
  };
};

// Hook for auth status only
export const useAuthStatus = () => {
  return useAppSelector(selectAuthStatus);
};

// Hook for API status only
export const useApiStatus = () => {
  return useAppSelector(selectApiConnectionStatus);
};
